import { useCallback } from "react";
import { useEffect, useMemo, useState } from "react";
import { useSettingsApiClient } from "@api/use-settings-api-client";
import { BaseProjectProps } from "@custom-types/project-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import { AddedDataComponent } from "@pages/data-management/added-data-component";
import { OptionsComponent } from "@pages/data-management/options-component";
import {
  AnyFilesInfo,
  DEFAULT_SETTING_PC,
  DEFAULT_SETTING_REFLECTION,
  DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  WorkflowType,
  SettingsResponsePC,
  SettingsResponseReflection,
  SettingsResponseWorkflowStopAfter,
  UploadedIdsMap,
} from "@pages/data-management/data-management-types";
import { getExistingFileTypes, getPublishSettings } from "@pages/data-management/data-management-utils";
import { ImportButtonsComponent } from "@pages/data-management/import-buttons-component";
import { SelectModeComponent } from "@pages/data-management/select-mode-component";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { sphereColors } from "@styles/common-colors";

/** Maps the DataManagement workflow type to the setting from SettingsAPI. */
const workflowTypeToStopAfterMapping: Record<WorkflowType, SettingsApiTypes.CaptureTreeWorkflowStopAfter> = {
  // "auto" means the workflow goes through the full process including "publishing".
  auto: "publish",
  // "staged" means the workflow stops after "registration", i.e. manual publishing.
  staged: "register",
};

interface Props extends BaseProjectProps {
  /** A map of the externalIds of the uploaded ELS scans, as returned by getUploadedIdsMap. */
  uploadedIdsMap: UploadedIdsMap;
  /** File infos constructed by useOnCheckFiles. */
  fileInfos: AnyFilesInfo[],
  /** Callback for setting the file infos. */
  setFileInfos: React.Dispatch<React.SetStateAction<AnyFilesInfo[]>>,
  /** Callback for setting the total file size, which is also shown in the upload page of the wizard. */
  setTotalFileSize: React.Dispatch<React.SetStateAction<number>>,
  /** Flag whether the browser's file explorer is currently open. */
  isFileExplorerOpen: boolean;
  /** Callback for setting whether the file explorer is open. */
  setIsFileExplorerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Flag whether file info is currently getting collected and the dropzone should show a spinner. */
  isDropzoneLoading: boolean;
  /** Callback for setting whether file info is getting collected. */
  setIsDropzoneLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/** The import settings page that is shown after the user has selected files to be uploaded. */
export function ImportSettingsPage({
  project,
  uploadedIdsMap,
  fileInfos,
  setFileInfos,
  setTotalFileSize,
  isFileExplorerOpen,
  setIsFileExplorerOpen,
  isDropzoneLoading,
  setIsDropzoneLoading,
}: Props): JSX.Element {
  const {
    isScreenLgAndLarger,
    isScreenXlOrLarger,
    isScreenSmOrSmaller,
  } = useMediaQueryList();
  const settingsApiClient = useSettingsApiClient();
  const [workflowType, setWorkflowType] = useState<WorkflowType>("auto");

  const [settingPC, setSettingPC] = useState<SettingsResponsePC>({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    value: DEFAULT_SETTING_PC,
  } as SettingsResponsePC);
  const [settingReflection, setSettingReflection] = useState<SettingsResponseReflection>({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- external package
    value: DEFAULT_SETTING_REFLECTION,
  } as SettingsResponseReflection);
  const [settingWorkflowStopAfter, setSettingWorkflowStopAfter] = useState<SettingsResponseWorkflowStopAfter>({
    value: DEFAULT_SETTING_WORKFLOW_STOP_AFTER,
  } as SettingsResponseWorkflowStopAfter);

  const existingFileTypes = useMemo(() => {
    return getExistingFileTypes(fileInfos);
  }, [fileInfos]);

  /**
   * Handles a change of the workflow type toggle button.
   * Updates the workflow type and the setting for stopping the workflow after registration or publishing.
   * @param workflowType The new workflow type.
   */
  const handleWorkflowTypeChange = useCallback((workflowType: WorkflowType) => {
    setWorkflowType(workflowType);
    setSettingWorkflowStopAfter((prev) => ({ ...prev,
      value: workflowTypeToStopAfterMapping[workflowType],
    }));
  }, [setSettingWorkflowStopAfter]);

  useEffect(() => {
    (async () => {
      const { settingPC, settingReflection, settingWorkflowStopAfter } = await getPublishSettings(project.id, settingsApiClient);
      setSettingPC(settingPC);
      setSettingReflection(settingReflection);
      // We don't care about the previous value, just set it from currently selected workflow type,
      // but it is still required to read it from SettingsApi as the `modifiedAt` is required here.
      settingWorkflowStopAfter.value = workflowTypeToStopAfterMapping[workflowType];
      setSettingWorkflowStopAfter(settingWorkflowStopAfter);
    })().catch(() => undefined);
  }, [settingsApiClient, project.id, workflowType]);

  let widthMiddle;
  if (isScreenXlOrLarger) {
    widthMiddle = "600px";
  } else if (isScreenLgAndLarger) {
    widthMiddle = "700px";
  } else {
    widthMiddle = "90%";
  }

  let widthRight;
  if (isScreenXlOrLarger) {
    widthRight = "480px";
  } else if (isScreenLgAndLarger) {
    widthRight = "700px";
  } else {
    widthRight = "90%";
  }

  return (
    <Box sx={{
        position: "fixed",
        top: "0px",
        left: "0px",
        width: "100%",
        height: "100%",
        overflow: "auto",
    }}>
      <Stack
        direction={ isScreenLgAndLarger ? "row" : "column" }
        sx={{
          minHeight: "calc(100% - 64px)",
          mt: "64px",
          alignItems: isScreenLgAndLarger ? undefined : "center",
          justifyContent: isScreenLgAndLarger ? undefined : "space-evenly",
          borderTop: `1px solid ${sphereColors.gray200}`,
      }}>
        <Box sx={{
          width: isScreenLgAndLarger ? "380px" : "90%",
          minWidth: isScreenLgAndLarger ? "380px" : undefined,
          maxWidth: isScreenLgAndLarger ? undefined : "740px",
          mt: isScreenLgAndLarger ? undefined : "30px",
          backgroundColor: sphereColors.pureWhite,
        }}>
          <AddedDataComponent
            project={project}
            uploadedIdsMap={uploadedIdsMap}
            fileInfos={fileInfos}
            setFileInfos={setFileInfos}
            isFileExplorerOpen={isFileExplorerOpen}
            setIsFileExplorerOpen={setIsFileExplorerOpen}
            isDropzoneLoading={isDropzoneLoading}
            setIsDropzoneLoading={setIsDropzoneLoading}
          />
        </Box>

        <Stack
          direction={ isScreenXlOrLarger ? "row" : "column" }
          sx={{
            alignItems: isScreenXlOrLarger ? undefined : "center",
            justifyContent: "space-evenly",
            width: "100%",
        }}>
          <Box sx={{
            width: widthMiddle,
            minWidth: isScreenLgAndLarger ? widthMiddle : undefined,
            maxWidth: isScreenLgAndLarger ? undefined : "700px",
            mt: isScreenLgAndLarger ? "50px" : "30px",
          }}>
            <SelectModeComponent
              workflowType={workflowType}
              handleWorkflowTypeChange={handleWorkflowTypeChange}
            />
          </Box>

          <Stack sx={{
            width: widthRight,
            minWidth: isScreenLgAndLarger ? widthRight : undefined,
            maxWidth: isScreenLgAndLarger ? undefined : "700px",
            mt: isScreenXlOrLarger ? "150px" : undefined,
          }}>
            <Box sx={{
              mb: isScreenSmOrSmaller ? "100px" : undefined,
            }}>
              <OptionsComponent
                workflowType={workflowType}
                existingFileTypes={existingFileTypes}
                settingPC={settingPC}
                setSettingPC={setSettingPC}
                settingReflection={settingReflection}
                setSettingReflection={setSettingReflection}
              />
            </Box>

            <ImportButtonsComponent
              project={project}
              workflowType={workflowType}
              existingFileTypes={existingFileTypes}
              settingPC={settingPC}
              settingReflection={settingReflection}
              settingWorkflowStopAfter={settingWorkflowStopAfter}
              fileInfos={fileInfos}
              setFileInfos={setFileInfos}
              setTotalFileSize={setTotalFileSize}
              isDropzoneLoading={isDropzoneLoading}
            />
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
